/**
 * CallSidebar — Panel lateral de llamadas Telnyx integrado en el CRM.
 *
 * Se desliza desde la derecha. Persiste entre rutas gracias al Zustand store.
 * Orquesta: WebRTC (useTelnyxWebRTC), transcripción (useLiveTranscription),
 * cuestionario interactivo y registro final de llamada.
 *
 * Layout:
 *   ┌─────────────────────────┐
 *   │ Header (contact+timer)  │
 *   │ Transcription (collaps) │
 *   │ Tabs: Quest | Transcript│
 *   │ Questionnaire / Result  │
 *   │ (scroll)                │
 *   └─────────────────────────┘
 */

import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

import { useCallStore } from '@/stores/useCallStore';
import { useTelnyxWebRTC } from '@/hooks/useTelnyxWebRTC';
import { useLiveTranscription } from '@/hooks/useLiveTranscription';
import { useRingbackTone } from '@/hooks/useRingbackTone';
import { AudioCaptureService } from '@/services/audioCapture/AudioCaptureService';
import { useGatheringAutoSave } from '@/hooks/useGatheringAutoSave';
import { prefetchTelnyxWebRTCToken } from '@/services/telnyxWebRTCService';
import { crmService } from '@/services/crmService';
import { completeCall as orchestratorCompleteCall } from '@/services/callOrchestrator';
import type { CallCreateRequest, ContactCreateRequest } from '@/types/crm';
import type { CallResultCode } from '@/types/callSidebar';

import { CallSidebarHeader } from './CallSidebarHeader';
import { CallSidebarTranscription } from './CallSidebarTranscription';
import { CallSidebarQuestionnaire } from './CallSidebarQuestionnaire';
import { CallSidebarResult } from './CallSidebarResult';
import { CallSidebarFollowUpEmails } from './CallSidebarFollowUpEmails';
import type { NextFollowUpValue } from './NextFollowUpSection';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function mapResultToCallStatus(result: CallResultCode): string {
  switch (result) {
    case 'answered_interested':
    case 'answered_not_interested':
      return 'completed';
    case 'no_answer':
    case 'voicemail':
      return 'no_answer';
    case 'wrong_number':
      return 'failed';
    case 'busy':
      return 'busy';
    default:
      return 'completed';
  }
}

// ─── HMR guard ───────────────────────────────────────────────────────────────
let _hmrGuardPhone: string | null = null;
let _hmrGuardTimer: ReturnType<typeof setTimeout> | null = null;

// ─── Component ───────────────────────────────────────────────────────────────

export function CallSidebar() {
  const store = useCallStore();
  const webrtc = useTelnyxWebRTC();
  const transcription = useLiveTranscription();
  const ringback = useRingbackTone();
  const [saving, setSaving] = useState(false);
  // Sprint 6 (follow-ups v2): próximo seguimiento programado desde el
  // form post-llamada. null cuando el agente no lo completó.
  const [nextFollowUp, setNextFollowUp] = useState<NextFollowUpValue | null>(
    null,
  );
  const callWasActiveRef = useRef(false);

  const { isOpen, phase, contact, questionnaire, seconds, startedAt, activeCallId } = store;

  // Auto-save gathering data to backend every 30s during active call
  useGatheringAutoSave(
    activeCallId,
    questionnaire,
    phase === "active" || phase === "post_call",
  );

  // ── Precarga al montar ─────────────────────────────────────────────────────
  useEffect(() => {
    AudioCaptureService.preloadWorklet();
    prefetchTelnyxWebRTCToken();
  }, []);

  // ── Auto-iniciar llamada cuando se abre con un contacto ────────────────────
  useEffect(() => {
    if (!isOpen || !contact || phase !== 'connecting') return;

    if (_hmrGuardTimer !== null) {
      clearTimeout(_hmrGuardTimer);
      _hmrGuardTimer = null;
    }

    if (_hmrGuardPhone === contact.phone) return;
    _hmrGuardPhone = contact.phone;

    webrtc.startCall(contact.phone);

    return () => {
      _hmrGuardTimer = setTimeout(() => {
        _hmrGuardPhone = null;
        _hmrGuardTimer = null;
      }, 300);
    };
  }, [isOpen, contact?.phone, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sincronizar estado WebRTC → store ──────────────────────────────────────
  useEffect(() => {
    if (webrtc.callState === 'active' && phase === 'connecting') {
      store.setActive();
      callWasActiveRef.current = true;
    }
    if ((webrtc.callState === 'done' || webrtc.callState === 'error') && (phase === 'active' || phase === 'connecting')) {
      store.setPostCall();
    }
    // Error pre-conexión
    if (webrtc.callState === 'error' && !callWasActiveRef.current && webrtc.errorMessage) {
      toast.error(webrtc.errorMessage.split('\n')[0]);
    }
  }, [webrtc.callState]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ringback tone: reproducir tonos mientras conecta/suena ─────────────
  // Simula la experiencia de un teléfono real: el agente escucha los tonos
  // de llamada durante connecting/ringing y se detienen al contestar o colgar.
  useEffect(() => {
    const st = webrtc.callState;
    if (st === 'connecting' || st === 'ringing') {
      ringback.start();
    } else {
      ringback.stop();
    }
  }, [webrtc.callState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup ringback al desmontar
  useEffect(() => {
    return () => { ringback.stop(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Detección automática de buzón: llamada corta (< 15s) que el remoto cuelga
  // Típico patrón de contestador: contesta automáticamente y cuelga tras el saludo.
  // Leemos del store directamente para evitar closures stale (seconds/questionnaire).
  useEffect(() => {
    if (webrtc.callState !== 'done') return;
    const currentState = useCallStore.getState();
    const currentSeconds = currentState.seconds;
    const currentResult = currentState.questionnaire.call_result;
    if (
      callWasActiveRef.current &&
      currentSeconds > 0 &&
      currentSeconds <= 15 &&
      !currentResult // no sobrescribir si ya eligió resultado (ej. botón Buzón manual)
    ) {
      store.updateQuestionnaire({ call_result: 'voicemail' });
      toast('Llamada muy corta — marcado como buzón de voz', {
        icon: '📬',
        description: 'Puedes cambiar el resultado si no fue un buzón.',
        duration: 5000,
      });
    }
  }, [webrtc.callState]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Iniciar transcripción cuando la llamada se activa ──────────────────────
  // IMPORTANTE: usar `activeCallId` del store (= call_id real creado por el backend
  // via orchestratorStartCall), NO `webrtc.callId` (que es un UUID fabricado en el
  // cliente). Si abres el WS con el UUID del cliente, el backend lanza
  // ForeignKeyViolationError al persistir cada chunk porque la fila crm_calls no existe.
  useEffect(() => {
    if (
      webrtc.callState === 'active' &&
      activeCallId &&
      !transcription.state.isCapturing
    ) {
      transcription.startCapture(activeCallId, webrtc.remoteStream);
    }
  }, [webrtc.callState, activeCallId, webrtc.remoteStream]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fix timing: agregar remoteStream tardío ────────────────────────────────
  useEffect(() => {
    if (webrtc.remoteStream && transcription.state.isCapturing) {
      transcription.addRemoteStream(webrtc.remoteStream);
    }
  }, [webrtc.remoteStream, transcription.state.isCapturing]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Detener transcripción al terminar ──────────────────────────────────────
  useEffect(() => {
    if (webrtc.callState === 'done' || webrtc.callState === 'error') {
      transcription.stopCapture();
    }
  }, [webrtc.callState, transcription.stopCapture]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Timer ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'active') return;
    const id = setInterval(() => store.tick(), 1000);
    return () => clearInterval(id);
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Transcripción completa como texto ──────────────────────────────────────
  const fullTranscriptionText = useMemo(() => {
    const finalSegments = transcription.state.segments.filter((s) => s.isFinal);
    if (finalSegments.length === 0) return '';
    return finalSegments
      .map((s) => {
        const speaker = s.channel === 'agent' ? 'Agente' : s.channel === 'customer' ? 'Cliente' : 'Desconocido';
        return `[${speaker}]: ${s.text}`;
      })
      .join('\n');
  }, [transcription.state.segments]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleHangup = useCallback(() => {
    transcription.stopCapture();
    webrtc.hangup();
    // setPostCall lo maneja el sync effect al detectar callState === 'done'
  }, [webrtc, transcription]);

  // ── Acción rápida: buzón de voz ──────────────────────────────────────────
  // Cuelga la llamada y pre-selecciona "voicemail" como resultado.
  const handleVoicemail = useCallback(() => {
    transcription.stopCapture();
    webrtc.hangup();
    store.updateQuestionnaire({ call_result: 'voicemail' });
    toast.info('Marcado como buzón de voz');
  }, [webrtc, transcription, store]);

  const handleClose = useCallback(() => {
    webrtc.reset();
    transcription.stopCapture();
    transcription.clearSegments();
    callWasActiveRef.current = false;
    _hmrGuardPhone = null;
    store.close();
  }, [webrtc, transcription, store]);

  const handleSave = useCallback(async () => {
    if (saving || !contact) return;
    setSaving(true);

    try {
      const result = questionnaire.call_result as CallResultCode;
      if (!result) {
        toast.error('Selecciona el resultado de la llamada');
        setSaving(false);
        return;
      }

      const transcriptionForApi = fullTranscriptionText || undefined;

      // Si ya existe un registro de llamada (creado por orchestrator), usar /complete
      if (activeCallId) {
        // Si el agente eligió fecha en la sección "Seguimiento" simple +
        // responsable (call_result no requería NextFollowUpSection), sintetizar
        // un next_follow_up para que la tarea SIEMPRE se cree con owner. Antes
        // se enviaba solo follow_up_date sin owner → tarea huérfana
        // (bug F-Llamada-1).
        let nextFollowUpToSend = nextFollowUp ?? undefined;
        if (
          !nextFollowUpToSend &&
          questionnaire.follow_up_date &&
          questionnaire.follow_up_responsible_id
        ) {
          // SLA al final del día elegido (15:00 local) para coincidir con la
          // convención de los presets de NextFollowUpSection.
          const slaDate = new Date(`${questionnaire.follow_up_date}T15:00:00`);
          nextFollowUpToSend = {
            next_action_type: 'call',
            owner_id: questionnaire.follow_up_responsible_id,
            sla_deadline: slaDate.toISOString(),
            notes: undefined,
          };
        }
        await orchestratorCompleteCall(activeCallId, {
          call_result: result,
          call_type: questionnaire.call_type || undefined,
          notes: questionnaire.notes || undefined,
          transcription: transcriptionForApi,
          questionnaire: {
            ...questionnaire,
            call_result: undefined, // ya va en call_result
            notes: undefined, // ya va en notes
          },
          follow_up_date: questionnaire.follow_up_date || undefined,
          grading_llamada: questionnaire.grading_llamada || undefined,
          grading_situacion: questionnaire.grading_situacion || undefined,
          servicio_propuesto: questionnaire.servicio_propuesto || undefined,
          // Sprint 6 (follow-ups v2): programar próximo seguimiento con
          // responsable + cuándo. Backend: CallOrchestrator crea FollowUp
          // + emite activity log en la misma tx.
          next_follow_up: nextFollowUpToSend,
        });
      } else {
        // Flujo legacy: crear nueva llamada (desde ficha de contacto sin orchestrator)
        const callData: CallCreateRequest = {
          entity_type: 'contacts',
          entity_id: contact.id,
          direction: 'outbound',
          phone: contact.phone,
          duration: seconds,
          call_status: mapResultToCallStatus(result),
          call_result: result,
          call_type: questionnaire.call_type || undefined,
          started_at: startedAt ?? new Date().toISOString(),
          ended_at: new Date().toISOString(),
          responsible_user_id: store.responsibleUserId ?? undefined,
          notes: questionnaire.notes || undefined,
          transcription: transcriptionForApi,
          resumen_llamada: [
            `Resultado: ${result.replace(/_/g, ' ')}`,
            questionnaire.servicio_propuesto ? `Servicio: ${questionnaire.servicio_propuesto}` : '',
            questionnaire.grading_llamada ? `Grading llamada: ${questionnaire.grading_llamada}` : '',
            questionnaire.grading_situacion ? `Grading situación: ${questionnaire.grading_situacion}` : '',
            questionnaire.notes ? `Notas: ${questionnaire.notes}` : '',
          ].filter(Boolean).join('. ') || undefined,
          proxima_llamada_fecha: questionnaire.follow_up_date || undefined,
          proxima_accion_fecha: questionnaire.proxima_accion_fecha || undefined,
        };

        await crmService.createCall(callData);
      }

      // Actualizar datos del contacto si es primera llamada con datos de intake
      if (contact.isFirstCall && contact.id) {
        const contactUpdate: Partial<ContactCreateRequest> = {};
        if (questionnaire.city) contactUpdate.city = questionnaire.city;
        if (questionnaire.state) contactUpdate.state = questionnaire.state;
        if (questionnaire.nacionalidad) contactUpdate.nacionalidad = questionnaire.nacionalidad;
        if (questionnaire.empadronado !== undefined) contactUpdate.empadronado = questionnaire.empadronado;
        if (questionnaire.tiene_familiares_espana !== undefined) contactUpdate.tiene_familiares_espana = questionnaire.tiene_familiares_espana;
        if (questionnaire.profesion) contactUpdate.position = questionnaire.profesion;
        if (questionnaire.trabajando_actualmente !== undefined) contactUpdate.trabaja_b = questionnaire.trabajando_actualmente;
        if (questionnaire.grading_llamada) contactUpdate.grading_llamada = questionnaire.grading_llamada;
        if (questionnaire.grading_situacion) contactUpdate.grading_situacion = questionnaire.grading_situacion;

        const customFields: Record<string, string> = {};
        if (questionnaire.servicio_propuesto) customFields.servicio_propuesto = questionnaire.servicio_propuesto;
        if (questionnaire.servicio_detalle) customFields.servicio_detalle = questionnaire.servicio_detalle;
        if (questionnaire.familiares_espana_detalle) customFields.familiares_espana_detalle = questionnaire.familiares_espana_detalle;
        if (questionnaire.tipo_trabajo_detalle) customFields.tipo_trabajo_detalle = questionnaire.tipo_trabajo_detalle;

        if (Object.keys(customFields).length > 0) {
          contactUpdate.custom_fields = customFields;
        }

        if (Object.keys(contactUpdate).length > 0) {
          try {
            await crmService.updateContact(contact.id, contactUpdate);
          } catch {
            // No bloquear el flujo si falla la actualización del contacto
            console.warn('[CallSidebar] No se pudieron actualizar datos del contacto');
          }
        }
      }

      toast.success('Llamada registrada correctamente');
      handleClose();
    } catch (err: any) {
      console.error('[CallSidebar] Error guardando llamada:', err);
      toast.error('Error al registrar la llamada');
    } finally {
      setSaving(false);
    }
  }, [saving, contact, questionnaire, seconds, startedAt, fullTranscriptionText, store, handleClose]);

  // ── No renderizar si está cerrado ──────────────────────────────────────────
  if (!isOpen || !contact) return null;

  const isPreConnectError = webrtc.callState === 'error' && !callWasActiveRef.current;

  return (
      <motion.aside
        key="call-sidebar"
        initial={{ x: '100%', opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: '100%', opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className={cn(
          'fixed top-0 right-0 h-full z-40',
          'w-full sm:w-[380px] lg:w-[400px]',
          'bg-white shadow-2xl border-l border-gray-200',
          'flex flex-col',
        )}
      >
        {/* Header */}
        <CallSidebarHeader
          contactName={contact.name}
          contactPhone={contact.phone}
          phase={phase}
          seconds={seconds}
          isMuted={webrtc.isMuted}
          remoteStream={webrtc.remoteStream}
          onToggleMute={() => webrtc.toggleMute()}
          onHangup={handleHangup}
          onClose={handleClose}
          onVoicemail={handleVoicemail}
        />

        {/* Error pre-conexión */}
        {isPreConnectError && webrtc.errorMessage && (
          <div className="px-4 py-3 bg-red-50 border-b border-red-200">
            <p className="text-xs text-red-600 font-medium">{webrtc.errorMessage.split('\n')[0]}</p>
            {webrtc.errorMessage.split('\n').slice(1).map((line, i) => (
              <p key={i} className="text-[11px] text-red-400">{line}</p>
            ))}
            <button
              type="button"
              onClick={handleClose}
              className="mt-2 text-xs text-red-700 font-semibold hover:underline"
            >
              Cerrar
            </button>
          </div>
        )}

        {/* Transcription (always visible during/after call) */}
        {(phase === 'active' || phase === 'post_call' || transcription.state.segments.length > 0) && (
          <CallSidebarTranscription
            segments={transcription.state.segments}
            wsStatus={transcription.state.wsStatus}
            isCapturing={transcription.state.isCapturing}
            error={transcription.state.error}
            defaultCollapsed={phase === 'post_call'}
          />
        )}

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">
          {/* Tabs (durante llamada activa o conectando) */}
          {(phase === 'connecting' || phase === 'active') && (
            <>
              {/* Tab bar */}
              <div className="flex border-b border-gray-200 px-4 sticky top-0 bg-white z-10">
                <button
                  type="button"
                  onClick={() => store.setActiveTab('questionnaire')}
                  className={cn(
                    'flex-1 py-2 text-xs font-semibold transition-colors border-b-2',
                    store.activeTab === 'questionnaire'
                      ? 'text-blue-600 border-blue-600'
                      : 'text-gray-500 border-transparent hover:text-gray-700',
                  )}
                >
                  Cuestionario
                </button>
                <button
                  type="button"
                  onClick={() => store.setActiveTab('transcription')}
                  className={cn(
                    'flex-1 py-2 text-xs font-semibold transition-colors border-b-2',
                    store.activeTab === 'transcription'
                      ? 'text-blue-600 border-blue-600'
                      : 'text-gray-500 border-transparent hover:text-gray-700',
                  )}
                >
                  Notas
                </button>
              </div>

              {/* Tab content */}
              {store.activeTab === 'questionnaire' && (
                <CallSidebarQuestionnaire
                  data={questionnaire}
                  onChange={store.updateQuestionnaire}
                  isFirstCall={contact.isFirstCall ?? false}
                />
              )}
              {store.activeTab === 'transcription' && (
                <div className="px-4 py-3">
                  <textarea
                    value={questionnaire.notes}
                    onChange={(e) => store.updateQuestionnaire({ notes: e.target.value })}
                    placeholder="Escribe notas detalladas durante la llamada..."
                    rows={12}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                </div>
              )}
            </>
          )}

          {/* Post-call: Resultado + Questionnaire (todavía editable) */}
          {phase === 'post_call' && (
            <>
              {/* Bloque "Enviar correo de seguimiento" — siempre visible en
                  post_call (acuerdo del 2026-05-20). Backend: ver
                  app/api/endpoints/crm/follow_up_emails.py en migrofast. */}
              <CallSidebarFollowUpEmails
                contactId={contact.id}
                contactEmail={contact.email ?? null}
              />

              <CallSidebarResult
                data={questionnaire}
                onChange={store.updateQuestionnaire}
                onSave={handleSave}
                saving={saving}
                transcriptionChars={fullTranscriptionText.length}
                contactId={contact.id}
                nextFollowUp={nextFollowUp}
                onNextFollowUpChange={setNextFollowUp}
              />

              {/* Questionnaire todavía visible/editable para completar datos */}
              <div className="border-t border-gray-200">
                <p className="px-4 pt-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                  Datos de la llamada (editable)
                </p>
                <CallSidebarQuestionnaire
                  data={questionnaire}
                  onChange={store.updateQuestionnaire}
                  isFirstCall={contact.isFirstCall ?? false}
                />
              </div>
            </>
          )}
        </div>
      </motion.aside>
  );
}
